const ExtractFormat = {
  DWG: 'DWG',
  DGN: 'DGN',
  KMZ: 'KMZ',
  SHP: 'SHP',
} as const;

type TExtractFormat = (typeof ExtractFormat)[keyof typeof ExtractFormat];

const ExtractFormatLabelMap = {
  DWG: 'AutoCAD DWG',
  DGN: 'Bentley MicroStation DGN',
  KMZ: 'Google Earth KMZ',
  SHP: 'ESRI Shapefile',
} as const satisfies Record<TExtractFormat, string>;

type TExtractFormatLabel = (typeof ExtractFormatLabelMap)[keyof typeof ExtractFormatLabelMap];

/**
 * Upper bound on formats per submission. A submission costs one extract run whatever it asks for, so
 * what this bounds is that run: how long it takes to write every format, and how large the combined
 * archive it produces gets. Pinned rather than derived from the enum, so adding a format is a
 * deliberate decision about that cost instead of a silently wider cap.
 *
 * Lives here, alongside the format enum and with no imports of its own, so browser code can import it
 * without pulling in the package barrel (which re-exports pino and redis).
 */
const MAX_EXTRACT_FORMATS = 4;

/** Longest project-name stem allowed in a stored file's name, leaving the key well inside S3's 1024 bytes. */
const MAX_EXPORT_STEM_LENGTH = 80;

/**
 * Filename stem for a project's stored exports. The project id means nothing to whoever opens the
 * download, so its name leads instead.
 *
 * Unsafe characters are dropped rather than substituted: a `/` would split the S3 key and a `.`
 * would read as a false extension. Accented Latin letters decompose first, so only the combining
 * mark is dropped. Falls back to the id when nothing survives.
 */
function exportFileStem(projectName: string | null | undefined, projectId: string) {
  const stem = (projectName ?? '')
    .normalize('NFD')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .replace(/([_-])\1+/g, '$1')
    .slice(0, MAX_EXPORT_STEM_LENGTH)
    .replace(/^[_-]+|[_-]+$/g, '');

  return stem || projectId;
}

/**
 * UTC stamp that makes one export's stored files distinguishable from the next export of the same
 * project, since the stem alone repeats. Second resolution in `yyMMddHHmmss`: short enough to keep
 * the name readable, and lexicographically sortable so a download folder orders by age on its own.
 *
 * No date library: this module must stay import-free (see above).
 */
function exportFileTimestamp(at: Date = new Date()) {
  const pad = (value: number) => String(value).padStart(2, '0');

  return [
    pad(at.getUTCFullYear() % 100),
    pad(at.getUTCMonth() + 1),
    pad(at.getUTCDate()),
    pad(at.getUTCHours()),
    pad(at.getUTCMinutes()),
    pad(at.getUTCSeconds()),
  ].join('');
}

// Static layer id → FME name map; the DB `layerIdToDownloadLayer` snapshot is the source of truth.
// Mirrors client/src/pages/projectDetails/utilities/common.ts.
const LEGACY_ExtractLayerMap = {
  buildings: 'Building Footprints',
  contourLines3ft: 'Contour Lines (full resolution)',
  electricTransmissionStructures: 'Electric Transmission Structures',
  electricTransmissionLines: 'Electric Transmission Lines',
  electricDistributionLines: 'Electric Distribution Lines',
  electricDistributionStructures: 'Electric Distribution Structures',
  electricDistributionServiceLines: 'Electric Distribution Service Lines',
  electricDistributionOther: 'Electric Distribution Other',
  electricDistributionServiceVaults: 'Electric Distribution Vaults',
  gasPoints: 'Gas Points',
  gasDistribution: 'Gas Distribution',
  gasTransmission: 'Gas_Transmission',
  gasService: 'Gas Service',
  // geotechnicalGroundwaterStations: 'Groundwater Stations', // TEMP removed
  environmentalSacramentoProtectedTrees: 'Sacramento Protected Trees',
  landRightsParcels: 'Parcels_Production',
  sanitaryPoints: 'Sanitary Points',
  sanitaryPipes: 'Sanitary Pipes',
  // soils: 'Soil Classification', // TEMP removed
  stormPoints: 'Storm Points',
  stormPipes: 'Storm Pipes',
  waterPoints: 'Water Points',
  waterMain: 'Water Main',
  waterTransmission: 'Water Transmission',
  waterService: 'Water Service',
  recycledWaterPipes: 'Recycled_Pipes',
  recycledWaterPoints: 'Recycled_Points',
  // geotechnicalOilGasWells: 'Wells', // TEMP removed
  landUseCurbData: 'Curb',
  fiberLitBuildings: 'Fiber Lit Buildings',
  cellTowers: 'Cell Towers',
  submarineLandings: 'Submarine Landings',
  fiberRoutes: 'Fiber Routes',
  submarineCables: 'Submarine Routes',
  // this currently requires approval from partner regardless of project specs, but we include it here to populate the layer list
  // it will eventually be less restrictive
  fossilFuelPipelines: 'Fossil Fuel Pipelines',

  // digitized datasets
  digitizedElectricVaults: 'Digitized_Electric_Vaults',
  digitizedElectricStructures: 'Digitized_Electric_Structures',
  digitizedElectricLines: 'Digitized_Electric_Lines',
  digitizedGasPoints: 'Digitized_Gas_Points',
  digitizedGasLines: 'Digitized_Gas_Pipes',
  digitizedSewerPoints: 'Digitized_Sewer_Points',
  digitizedSewerLines: 'Digitized_Sewer_Lines',
  digitizedWaterPoints: 'Digitized_Water_Points',
  digitizedWaterLines: 'Digitized_Water_Lines',
  digitizedRecycledWaterLines: 'Digitized_Recycled_Water_Lines',
  digitizedRecycledWaterPoints: 'Digitized_Recycled_Water_Points',
  digitizedStormPoints: 'Digitized_Storm_Points',
  digitizedStormLines: 'Digitized_Storm_Lines',
  digitizedTelcoLines: 'Digitized_Telco_Lines',
  digitizedTelcoPoints: 'Digitized_Telco_Points',
  digitizedFuelPoints: 'Digitized_Fuel_Points',
  digitizedFuelLines: 'Digitized_Fuel_Lines',
  digitizedTrafficSignals: 'Digitized_Signal_Points',
  digitizedTrafficSignalLines: 'Digitized_Signal_Lines',
  digitizedOtherPoints: 'Digitized_Other_Points',
  digitizedOtherLines: 'Digitized_Other_Lines',
  digitizedEasements: 'Digitized_Easements',

  // SCE (Special Dataset)
  sceElectricPoints: 'SCE_Electric_point',
  sceStormPoints: 'SCE_Storm_point',
  scePropertyPoints: 'SCE_Property_point',
  sceWaterPoints: 'SCE_Water_point',
  sceLandscapePoints: 'SCE_Landscape_point',
  sceOtherUtilityPoints: 'SCE_Other_Utility_point',
  sceOtherPoints: 'SCE_Other_point',
  sceStreetPoints: 'SCE_Street_point',
  sceSewerPoints: 'SCE_Sewer_point',
  sceSurveyPoints: 'SCE_Survey_point',
  sceGasAndOilPoints: 'SCE_Gas_Oil_point',
  sceElectricOtherLines: 'SCE_Electric_Other_polylines',
  sceStormLines: 'SCE_Storm_polylines',
  scePropertyLines: 'SCE_Property_polylines',
  sceWaterLines: 'SCE_Water_polylines',
  sceLandscapeLines: 'SCE_Landscape_polylines',
  sceOtherUtilityLines: 'SCE_Other_Utility_polylines',
  sceOtherLines: 'SCE_Other_polylines',
  sceStreetLines: 'SCE_Street_polylines',
  sceSewerLines: 'SCE_Sewer_polylines',
  sceSurveyLines: 'SCE_Survey_polylines',
  sceGasAndOilLines: 'SCE_Gas_Oil_polylines',
  sceTelcoLines: 'SCE_Telco_polylines',
  sceAnnotationPoints: 'SCE_Annotation_point',
  sceTelcoPoints: 'SCE_Telco_point',
  sceElectricInstallLines: 'SCE_Electric_Install_polylines',
  sceElectricRemoveLines: 'SCE_Electric_Remove_polylines',
  sceElectricExistingLines: 'SCE_Electric_Existing_polylines',
  sceDimensionPoints: 'SCE_Dimension_point',
  sceDimensionLines: 'SCE_Dimension_polylines',
} as const;

type T_LEGACY_ExtractLayerMap = (typeof LEGACY_ExtractLayerMap)[keyof typeof LEGACY_ExtractLayerMap];

const FMEJobStatus = {
  Queued: 'queued',
  Running: 'running',
  Success: 'success',
  Failure: 'failure',
  Cancelled: 'cancelled',
} as const;

type TFMEJobStatus = (typeof FMEJobStatus)[keyof typeof FMEJobStatus];

/* ----------------------------------------------------------------------------------------- */
/* ---------- TEMP until we move off of "downloads" to "extracts" in the database ---------- */
/* ----------------------------------------------------------------------------------------- */

const EsriJobStatus = {
  Submitted: 'esriJobSubmitted',
  Executing: 'esriJobExecuting',
  Succeeded: 'esriJobSucceeded',
  Failed: 'esriJobFailed',
} as const;

type TEsriJobStatus = (typeof EsriJobStatus)[keyof typeof EsriJobStatus];

const FMEJobStatusToEsriJobStatusMap = {
  [FMEJobStatus.Queued]: EsriJobStatus.Submitted,
  [FMEJobStatus.Running]: EsriJobStatus.Executing,
  [FMEJobStatus.Success]: EsriJobStatus.Succeeded,
  [FMEJobStatus.Failure]: EsriJobStatus.Failed,
} as const;

/* ----------------------------------------------------------------------------------------- */
/* ------------------------------------- End temp work ------------------------------------- */
/* ----------------------------------------------------------------------------------------- */

export {
  ExtractFormat,
  type TExtractFormat,
  MAX_EXTRACT_FORMATS,
  exportFileStem,
  exportFileTimestamp,
  LEGACY_ExtractLayerMap,
  type T_LEGACY_ExtractLayerMap,
  ExtractFormatLabelMap,
  type TExtractFormatLabel,
  FMEJobStatus,
  type TFMEJobStatus,
  FMEJobStatusToEsriJobStatusMap,
  EsriJobStatus,
  type TEsriJobStatus,
};
